import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Divider,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';
import MetricTree from './MetricTree';


/* ===== SAFE DEFAULT ===== */
const emptyStats = {
    byRankType: {},
    availableByRank: [],
    numDays: 1,
    byPostBase: {},
    byBasePost: {},
};

/* ===== GET COLOR ===== */
const sickColor = [
    { min: 0, max: 0, color: 'text.primary' },
    { min: 1, max: 15, color: 'success.main' },
    { min: 16, max: 31, color: 'warning.main' },
    { min: 32, max: Infinity, color: 'error.main' },
];

const noShowColor = [
    { min: 0, max: 0, color: 'text.primary' },
    { min: 1, max: 3, color: 'success.main' },
    { min: 4, max: 31, color: 'warning.main' },
    { min: 32, max: Infinity, color: 'error.main' },
];

const getColorByValue = (value, colors) =>
    colors.find(r => value >= r.min && value <= r.max)?.color
    ?? 'text.primary';

/* ===== METRIC COLOR OVERRIDES (by categoryCode) ===== */
const METRIC_COLOR_PALETTES = {
    SL: sickColor,
    NSH: noShowColor,
};


const availableColor = (available, active) => {
    if (active === 0) return 'text.secondary';
    const rate = available / active;
    if (rate >= 0.9) return 'success.main';
    if (rate >= 0.7) return 'warning.main';
    return 'error.main';
};

const CrewStats = ({ data }) => {
    const stats = data ?? emptyStats;
    const { byRankType, availableByRank = [], numDays = 1 } = stats;

    const activeConfig = (data?.visibleCategories ?? []).map(({ categoryCode, description }) => ({
        postBaseKey: `by${categoryCode}PostBase`,
        basePostKey: `by${categoryCode}BasePost`,
        metricKey: `${categoryCode}Days`,
        metricLabel: categoryCode,
        title: description || categoryCode,
        colorPalette: METRIC_COLOR_PALETTES[categoryCode],
    }));

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {/* ===== BY RANK TYPE ===== */}
            <Card>
                <CardContent>
                    <Typography variant="h6" color="primary.dark" gutterBottom>
                        Flying Crew
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    {Object.entries(byRankType).length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            No data available
                        </Typography>
                    )}
                    {Object.entries(byRankType)
                        .sort(([a], [b]) => b.localeCompare(a))
                        .map(([type, count]) => (
                            <Chip key={type} label={`${type}: ${count}`} sx={{ mr: 1 }} />
                        ))}
                </CardContent>
            </Card>

            {/* ===== AVAILABLE BY RANK ===== */}
            <Card>
                <CardContent>
                    <Box 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="space-between" 
                        mb={1}
                    >
                        <Typography variant="h6" color="primary.dark">
                            Available Crew
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Active − NonFlying Days / {numDays}d
                        </Typography>
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    {Object.entries(
                        availableByRank.reduce((acc, row) => {
                            if (!row.rankType) return acc;
                            acc[row.rankType] = (acc[row.rankType] || 0) + (row.available || 0);
                            return acc;
                        }, {})
                    )
                        .sort(([a], [b]) => b.localeCompare(a))
                        .map(([type, count]) => (
                            <Chip 
                                key={type} 
                                label={`${type}: ${count?.toFixed(1)}`} 
                                sx={{ mr: 1 }} 
                            />
                        ))
                    }
                    <Divider sx={{ mt: 1 }} />
                    {availableByRank.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No data available
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ '& td, & th': { verticalAlign: 'middle' } }}>
                                    <TableCell sx={{ fontWeight: 'bold', py: 1 }}>
                                        Rank
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', py: 1 }}>
                                        Active
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', py: 1 }}>
                                        NonFlying
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', py: 1 }}>
                                        Available
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(() => {
                                    const groups = availableByRank.reduce((acc, row) => {
                                        const key = row.rankType || '';
                                        if (!acc[key]) acc[key] = [];
                                        acc[key].push(row);
                                        return acc;
                                    }, {});
                                    const sortedGroups = Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
                                    const totalActive = availableByRank.reduce((s, r) => s + (r.active || 0), 0);
                                    const totalNonFlying = availableByRank.reduce((s, r) => s + (r.nonflyingDays || 0), 0);
                                    const totalAvailable = availableByRank.reduce((s, r) => s + (r.available || 0), 0);

                                    const subtotalSx = { py: 0.5, bgcolor: 'action.hover' };
                                    const totalSx = { 
                                        py: 0.5, 
                                        bgcolor: 'action.selected', 
                                        fontWeight: 'bold', 
                                        borderTop: '2px solid', 
                                        borderColor: 'divider', 
                                    };

                                    return (
                                        <>
                                            {sortedGroups.map(([type, rows]) => {
                                                const subActive = rows.reduce((s, r) => s + (r.active || 0), 0);
                                                const subNonFlying = rows.reduce((s, r) => s + (r.nonflyingDays || 0), 0);
                                                const subAvailable = rows.reduce((s, r) => s + (r.available || 0), 0);
                                                return (
                                                    <React.Fragment key={type}>
                                                        {rows.map(row => (
                                                            <TableRow key={row.rank} hover>
                                                                <TableCell sx={{ py: 0.5 }}>
                                                                    <Typography variant="body2" lineHeight={1.2}>
                                                                        {row.rank}
                                                                    </Typography>
                                                                    {row.rankType && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {row.rankType}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ py: 0.5 }}>
                                                                    <Typography variant="body2">{row.active}</Typography>
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ py: 0.5 }}>
                                                                    <Typography variant="body2">
                                                                        {(row.nonflyingDays / numDays).toFixed(1)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ py: 0.5 }}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color={availableColor(row.available, row.active)}
                                                                    >
                                                                        {row.available}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                        <TableRow>
                                                            <TableCell sx={subtotalSx}>
                                                                <Typography variant="body2" lineHeight={1.2}>
                                                                    Subtotal
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {type || 'Other'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right" sx={subtotalSx}>
                                                                {subActive}
                                                            </TableCell>
                                                            <TableCell align="right" sx={subtotalSx}>
                                                                {(subNonFlying / numDays)?.toFixed(1)}
                                                            </TableCell>
                                                            <TableCell align="right" sx={subtotalSx}>
                                                                <Typography
                                                                    variant="body2"
                                                                    color={availableColor(subAvailable, subActive)}
                                                                >
                                                                    {subAvailable?.toFixed(1)}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                );
                                            })}
                                            <TableRow>
                                                <TableCell sx={totalSx}>Total</TableCell>
                                                <TableCell align="right" sx={totalSx}>
                                                    {totalActive}
                                                </TableCell>
                                                <TableCell align="right" sx={totalSx}>
                                                    {(totalNonFlying / numDays)?.toFixed(1)}
                                                </TableCell>
                                                <TableCell align="right" sx={totalSx}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        color={availableColor(totalAvailable, totalActive)}
                                                    >
                                                        {totalAvailable?.toFixed(1)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    );
                                })()}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* ===== METRIC TREES ===== */}
            {activeConfig.map(({ postBaseKey, basePostKey, metricKey, metricLabel, title, colorPalette }) => {
                const colorFn = colorPalette ? (v) => getColorByValue(v, colorPalette) : undefined;

                return (
                    <React.Fragment key={metricKey}>
                        <MetricTree
                            title={`${title} by Post → Base`}
                            data={stats[postBaseKey]}
                            metricKey={metricKey}
                            metricLabel={metricLabel}
                            level1Label="Post"
                            level2Label="Base"
                            colorFn={colorFn}
                        />
                        <MetricTree
                            title={`${title} by Base → Post`}
                            data={stats[basePostKey]}
                            metricKey={metricKey}
                            metricLabel={metricLabel}
                            level1Label="Base"
                            level2Label="Post"
                            colorFn={colorFn}
                        />
                    </React.Fragment>
                );
            })}
        </Box>
    );
}

export default CrewStats;
